"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _ExpandCollapse = _interopRequireDefault(require("./ExpandCollapse"));
var _OrderedListBase = _interopRequireDefault(require("../OrderedList/OrderedListBase"));
var _utils = require("../utils");
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const ContentContainer = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "TermsAndConditions__ContentContainer",
  componentId: "components-web__sc-1199epn-0"
})(_ref => {
  let {
    tokens
  } = _ref;
  return {
    paddingBottom: tokens.contentPaddingBottom,
    paddingLeft: tokens.contentPaddingLeft,
    ...(0, _utils.media)().from('md').css({
      paddingBottom: tokens.mdContentPaddingBottom,
      paddingLeft: tokens.mdContentPaddingLeft
    })
  };
});
const Ordered = /*#__PURE__*/(0, _styledComponents.default)(_OrderedListBase.default).withConfig({
  displayName: "TermsAndConditions__Ordered",
  componentId: "components-web__sc-1199epn-1"
})(_ref2 => {
  let {
    tokens
  } = _ref2;
  return {
    listStylePosition: 'outside',
    padding: tokens.orderedPadding
  };
});
const Unordered = /*#__PURE__*/_styledComponents.default.ul.withConfig({
  displayName: "TermsAndConditions__Unordered",
  componentId: "components-web__sc-1199epn-2"
})(_ref3 => {
  let {
    tokens
  } = _ref3;
  return {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    paddingTop: tokens.unorderedPadding
  };
});
const ListItem = /*#__PURE__*/(0, _styledComponents.default)(_OrderedListBase.default.Item).withConfig({
  displayName: "TermsAndConditions__ListItem",
  componentId: "components-web__sc-1199epn-3"
})(_ref4 => {
  let {
    tokens
  } = _ref4;
  return {
    display: 'list-item',
    '&::marker': {
      fontFamily: `${tokens.listFontName}${tokens.listFontWeight}normal`,
      fontSize: tokens.listFontSize,
      lineHeight: tokens.listLineHeight,
      textAlign: 'end !important'
    },
    color: tokens.listColor,
    fontFamily: `${tokens.listFontName}${tokens.listFontWeight}normal`,
    fontSize: tokens.listFontSize,
    lineHeight: tokens.listLineHeight,
    marginBottom: tokens.listMarginBottom,
    marginLeft: tokens.listMarginLeft,
    wordBreak: 'break-word'
  };
});
const NonIndexedContentTitle = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "TermsAndConditions__NonIndexedContentTitle",
  componentId: "components-web__sc-1199epn-4"
})(_ref5 => {
  let {
    tokens
  } = _ref5;
  return {
    color: tokens.titleColor,
    fontSize: tokens.titleFontSize,
    lineHeight: tokens.titleLineHeight,
    paddingLeft: tokens.titlePaddingLeft
  };
});

/**
 * Use `TermsAndConditions` to display important legal content.
 *
 * ## Usage Criteria
 *
 * - Display before the page footer, except for call-to-action callback cards
 * - Responsive display based on breakpoints
 * - Use `copy` to set language, ‘en’ for English or ‘fr’ for French
 */
const TermsAndConditions = /*#__PURE__*/_react.default.forwardRef((_ref6, ref) => {
  let {
    copy = 'en',
    indexedContent = [],
    nonIndexedContent = [],
    tokens = {},
    variant = {},
    dictionary = _dictionary.default,
    ...rest
  } = _ref6;
  const getCopy = (0, _componentsBase.useCopy)({
    dictionary,
    copy
  });
  const hasIndexedContent = indexedContent.length > 0;
  const hasNonIndexedContent = nonIndexedContent.length > 0;
  const viewport = (0, _componentsBase.useViewport)();
  const {
    themeOptions
  } = (0, _componentsBase.useTheme)();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? _componentsBase.useResponsiveThemeTokens : _componentsBase.useThemeTokens;
  const themeTokens = useTokens('TermsAndConditions', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  let listItemStyles;
  let listItemMediaIds;
  let nonIndexedContentStyles;
  let nonIndexedContentMediaIds;
  if (enableMediaQueryStyleSheet) {
    const {
      transformedListItemThemeTokens,
      transformedNonIndexedContentThemeTokens
    } = Object.entries(themeTokens).reduce((acc, _ref7) => {
      let [vp, viewportTokens] = _ref7;
      acc.transformedListItemThemeTokens[vp] = {
        marginLeft: viewportTokens.listMarginLeft
      };
      acc.transformedNonIndexedContentThemeTokens[vp] = {
        paddingLeft: viewportTokens.titlePaddingLeft
      };
      return acc;
    }, {
      transformedListItemThemeTokens: {},
      transformedNonIndexedContentThemeTokens: {}
    });
    const listItemMediaQueryStyles = (0, _componentsBase.createMediaQueryStyles)(transformedListItemThemeTokens);
    const nonIndexedContentMediaQueryStyles = (0, _componentsBase.createMediaQueryStyles)(transformedNonIndexedContentThemeTokens);
    const {
      ids,
      styles
    } = _componentsBase.StyleSheet.create({
      listItem: {
        ...themeTokens[viewport],
        ...listItemMediaQueryStyles
      },
      nonIndexedContent: {
        ...themeTokens[viewport],
        ...nonIndexedContentMediaQueryStyles
      }
    });
    listItemStyles = styles.listItem;
    listItemMediaIds = ids.listItem;
    nonIndexedContentStyles = styles.nonIndexedContent;
    nonIndexedContentMediaIds = ids.nonIndexedContent;
  } else {
    listItemStyles = themeTokens;
    nonIndexedContentStyles = themeTokens;
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    ...selectProps(rest),
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Divider, {
      tokens: {
        color: themeTokens.dividerColor
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ExpandCollapse.default, {
      collapseTitle: getCopy('headingView'),
      expandTitle: getCopy('headingHide'),
      collapseLabel: getCopy('collapseLabel'),
      expandLabel: getCopy('expandLabel'),
      ref: ref,
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(ContentContainer, {
        tokens: themeTokens,
        children: [hasIndexedContent && /*#__PURE__*/(0, _jsxRuntime.jsx)(Ordered, {
          tokens: themeTokens,
          children: indexedContent.map((contentItem, idx) =>
          /*#__PURE__*/
          // eslint-disable-next-line react/no-array-index-key
          (0, _jsxRuntime.jsx)(ListItem, {
            tokens: listItemStyles,
            media: listItemMediaIds,
            children: (0, _utils.renderStructuredContent)(contentItem)
          }, idx))
        }), hasNonIndexedContent && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_componentsBase.Box, {
          between: 3,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(NonIndexedContentTitle, {
            tokens: nonIndexedContentStyles,
            media: nonIndexedContentMediaIds,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
              block: true,
              heading: true,
              tokens: {
                fontName: themeTokens.expandTitleFontName,
                fontWeight: themeTokens.expandTitleFontWeight,
                fontSize: themeTokens.titleFontSize,
                lineHeight: themeTokens.titleLineHeight
              },
              children: getCopy('nonIndexedTitle')
            })
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Unordered, {
            tokens: themeTokens,
            children: nonIndexedContent.map((contentItem, idx) =>
            /*#__PURE__*/
            // eslint-disable-next-line react/no-array-index-key
            (0, _jsxRuntime.jsx)(ListItem, {
              tokens: listItemStyles,
              media: listItemMediaIds,
              children: (0, _utils.renderStructuredContent)(contentItem)
            }, idx))
          })]
        })]
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Divider, {
      tokens: {
        color: themeTokens.dividerColor
      }
    })]
  });
});
TermsAndConditions.displayName = 'TermsAndConditions';

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = _propTypes.default.shape({
  headingHide: _propTypes.default.string.isRequired,
  headingView: _propTypes.default.string.isRequired,
  nonIndexedTitle: _propTypes.default.string.isRequired
});
TermsAndConditions.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Use the `copy` prop to either select provided English or French copy by passing 'en' or 'fr' respectively.
   *
   * To provide your own, pass a JSON object with the keys `headingView`, `headingHide`, and `nonIndexedTitle`.
   */
  copy: _propTypes.default.oneOfType([_propTypes.default.oneOf(['en', 'fr']), _propTypes.default.shape({
    headingView: _propTypes.default.string,
    headingHide: _propTypes.default.string,
    nonIndexedTitle: _propTypes.default.string
  })]),
  /**
   * Sets the tokens for TermsAndConditions element.
   */
  tokens: (0, _componentsBase.getTokensPropType)('TermsAndConditions'),
  /**
   * An array of nodes, strings, or a combination to be displayed in an ordered list.
   *
   * Each item in the array must have a corresponding superscript in the page.
   */
  indexedContent: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.string])),
  /**
   * An array of nodes, strings, or a combination to be displayed in an unordered list.
   *
   * nonIndexedContent do not have a corresponding superscript and instead apply to the page as a whole.
   */
  nonIndexedContent: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.string])),
  /**
   * Custom dictionary containing the labels to use for `TermsAndConditions`
   */
  dictionary: _propTypes.default.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  })
};
var _default = exports.default = TermsAndConditions;